import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, ClockCountdown } from "@phosphor-icons/react";

const CAT_LABELS = {
  payment_reminder: "Payment Reminder", due_today: "Due Today", overdue: "Overdue",
  payment_confirmation: "Payment Confirmation", welcome: "Welcome", settlement: "Settlement",
  salary_day_reminder: "Salary Day Reminder", reloan_eligibility: "Re-Loan Eligibility",
  general: "General", custom: "Custom",
};

export default function EmailRules() {
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [days, setDays] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/connect/email/rules");
      setR(data.rules); setDays(data.day_labels || []);
    } catch { toast.error("Could not load rules"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setR((p) => ({ ...p, [k]: v }));
  const toggleDay = (idx) => {
    const cur = new Set(r.allowed_days || []);
    cur.has(idx) ? cur.delete(idx) : cur.add(idx);
    set("allowed_days", Array.from(cur).sort());
  };
  const toggleExempt = (cat) => {
    const cur = new Set(r.exempt_categories || []);
    cur.has(cat) ? cur.delete(cat) : cur.add(cat);
    set("exempt_categories", Array.from(cur));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/connect/email/rules", {
        timezone: r.timezone, allowed_days: r.allowed_days, window_start: r.window_start,
        window_end: r.window_end, allow_outside_window: r.allow_outside_window,
        freq_per_day: Number(r.freq_per_day), freq_per_7d: Number(r.freq_per_7d),
        freq_per_30d: Number(r.freq_per_30d), min_gap_hours: Number(r.min_gap_hours),
        duplicate_protection_minutes: Number(r.duplicate_protection_minutes),
        exempt_categories: r.exempt_categories, once_only_categories: r.once_only_categories,
      });
      setR(data.rules);
      toast.success("Communication rules saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!r) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const Num = ({ k, label, suffix }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <Input type="number" min="0" value={r[k]} onChange={(e) => set(k, e.target.value)} className="rounded-sm w-24" data-testid={`rules-${k}`} />
        {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div data-testid="email-rules-page">
      <button onClick={() => nav("/connect")} className="text-xs text-muted-foreground flex items-center gap-1 mb-3 hover:text-foreground" data-testid="rules-back">
        <ArrowLeft size={14} weight="bold" /> Back to Connect
      </button>
      <div className="flex items-center gap-2 mb-1">
        <ClockCountdown size={22} weight="bold" className="text-primary" />
        <h1 className="text-2xl font-bold tracking-tight">Email Rules & Frequency</h1>
      </div>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs px-2 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200 font-semibold">RecoverOS Recommended Defaults</span>
        <p className="text-sm text-muted-foreground">Review and adjust to match your own policies. These are not legally mandated.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Sending window */}
        <div className="border border-border rounded-sm p-4 bg-card space-y-4">
          <h2 className="font-bold text-sm">Allowed Sending Window</h2>
          <div>
            <Label className="text-xs">Timezone</Label>
            <Input value={r.timezone} onChange={(e) => set("timezone", e.target.value)} className="rounded-sm mt-1 w-56" data-testid="rules-timezone" />
            <p className="text-[11px] text-muted-foreground mt-1">All rules & scheduling use this timezone (not the server's).</p>
          </div>
          <div>
            <Label className="text-xs">Allowed Days</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {days.map((d, i) => {
                const on = (r.allowed_days || []).includes(i);
                return (
                  <button key={i} onClick={() => toggleDay(i)} data-testid={`rules-day-${i}`}
                    className={`px-2.5 py-1 rounded-sm text-xs font-semibold border transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    {d.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs">Start</Label>
              <Input type="time" value={r.window_start} onChange={(e) => set("window_start", e.target.value)} className="rounded-sm mt-1 w-32" data-testid="rules-window-start" />
            </div>
            <div>
              <Label className="text-xs">End</Label>
              <Input type="time" value={r.window_end} onChange={(e) => set("window_end", e.target.value)} className="rounded-sm mt-1 w-32" data-testid="rules-window-end" />
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div>
              <div className="text-xs font-semibold">Allow sending outside permitted hours</div>
              <p className="text-[11px] text-muted-foreground">Off = sends outside the window are blocked/scheduled.</p>
            </div>
            <Switch checked={!!r.allow_outside_window} onCheckedChange={(v) => set("allow_outside_window", v)} data-testid="rules-allow-outside" />
          </div>
        </div>

        {/* Frequency */}
        <div className="border border-border rounded-sm p-4 bg-card space-y-4">
          <h2 className="font-bold text-sm">Frequency & Gap (per customer)</h2>
          <div className="grid grid-cols-3 gap-3">
            <Num k="freq_per_day" label="Max / day" />
            <Num k="freq_per_7d" label="Max / 7 days" />
            <Num k="freq_per_30d" label="Max / 30 days" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Num k="min_gap_hours" label="Minimum gap" suffix="hours" />
            <Num k="duplicate_protection_minutes" label="Duplicate window" suffix="minutes" />
          </div>
          <div className="border-t border-border pt-3">
            <div className="text-xs font-semibold mb-2">Exempt from frequency & time rules</div>
            <p className="text-[11px] text-muted-foreground mb-2">Transactional emails (e.g. Payment Confirmation) can bypass caps when you enable them here.</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(CAT_LABELS).map((cat) => {
                const on = (r.exempt_categories || []).includes(cat);
                return (
                  <button key={cat} onClick={() => toggleExempt(cat)} data-testid={`rules-exempt-${cat}`}
                    className={`px-2.5 py-1 rounded-sm text-xs font-semibold border transition-colors ${on ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    {CAT_LABELS[cat]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="rules-save">
          <FloppyDisk size={15} weight="bold" className="mr-1.5" /> {saving ? "Saving…" : "Save Rules"}
        </Button>
      </div>
    </div>
  );
}
